
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {BlockedIPReason} from './BlockedIPReason';
import {FieldModelBlockedIPReason} from './BlockedIPReason';

/**
 * BlockedIP struct
 */
class BlockedIP {
  /**
   * Initialize struct
   * @param {!Array=} iPv4Address
   * @param {!Date=} fromInclusive
   * @param {!Date=} toExclusive
   * @param {!BlockedIPReason=} reason
   * @constructor
   */
  constructor (iPv4Address = [], fromInclusive = new Date(0), toExclusive = new Date(0), reason = new BlockedIPReason()) {
    this.IPv4Address = iPv4Address
    this.FromInclusive = fromInclusive
    this.ToExclusive = toExclusive
    this.Reason = reason
  }

  /**
   * Copy struct (shallow copy)
   * @this {!BlockedIP}
   * @param {!BlockedIP} other Other struct
   * @returns {!BlockedIP} This struct
   */
  copy (other) {
    if (other.IPv4Address != null) {
      this.IPv4Address = []
      for (let item of other.IPv4Address) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.IPv4Address.push(tempItem)
        } else {
          this.IPv4Address.push(null)
        }
      }
    } else {
      this.IPv4Address = null
    }
    if (other.FromInclusive != null) {
      if (other.FromInclusive instanceof Date) {
        this.FromInclusive = new Date(other.FromInclusive.getTime())
      } else {
        this.FromInclusive = new Date(Math.round(other.FromInclusive / 1000000))
      }
    } else {
      this.FromInclusive = null
    }
    if (other.ToExclusive != null) {
      if (other.ToExclusive instanceof Date) {
        this.ToExclusive = new Date(other.ToExclusive.getTime())
      } else {
        this.ToExclusive = new Date(Math.round(other.ToExclusive / 1000000))
      }
    } else {
      this.ToExclusive = null
    }
    if (other.Reason != null) {
      this.Reason = BlockedIPReason.fromObject(other.Reason)
    } else {
      this.Reason = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BlockedIP}
   * @returns {!BlockedIP} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BlockedIPModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BlockedIPModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BlockedIP}
   * @param {!BlockedIP} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BlockedIP)) {
      throw new TypeError('Instance of BlockedIP is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BlockedIP}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      IPv4Address: ((this.IPv4Address != null) ? Array.from(this.IPv4Address, item => ((item != null) ? item : null)) : null),
      FromInclusive: ((this.FromInclusive != null) ? (this.FromInclusive.getTime() * 1000000) : null),
      ToExclusive: ((this.ToExclusive != null) ? (this.ToExclusive.getTime() * 1000000) : null),
      Reason: ((this.Reason != null) ? this.Reason : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BlockedIP.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BlockedIP} other Object value
   * @returns {!BlockedIP} Created struct
   */
  static fromObject (other) {
    return new BlockedIP().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BlockedIP}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BlockedIP.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BlockedIP}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6554
  }
}

export { BlockedIP };

/**
 * Fast Binary Encoding BlockedIP field model
 */
class FieldModelBlockedIP extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.IPv4Address = new fbe.FieldModelArray(new fbe.FieldModelByte(buffer, 4 + 4), buffer, 4 + 4, 4)
    this.FromInclusive = new fbe.FieldModelTimestamp(buffer, this.IPv4Address.fbeOffset + this.IPv4Address.fbeSize)
    this.ToExclusive = new fbe.FieldModelTimestamp(buffer, this.FromInclusive.fbeOffset + this.FromInclusive.fbeSize)
    this.Reason = new FieldModelBlockedIPReason(buffer, this.ToExclusive.fbeOffset + this.ToExclusive.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelBlockedIP}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBlockedIP}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.IPv4Address.fbeSize + this.FromInclusive.fbeSize + this.ToExclusive.fbeSize + this.Reason.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBlockedIP}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.IPv4Address.fbeExtra + this.FromInclusive.fbeExtra + this.ToExclusive.fbeExtra + this.Reason.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBlockedIP}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBlockedIP.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBlockedIP}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6554
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBlockedIP}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBlockedIP}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IPv4Address.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IPv4Address.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IPv4Address.fbeSize

    if ((fbeCurrentSize + this.FromInclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FromInclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FromInclusive.fbeSize

    if ((fbeCurrentSize + this.ToExclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ToExclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToExclusive.fbeSize

    if ((fbeCurrentSize + this.Reason.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Reason.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Reason.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBlockedIP}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBlockedIP}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBlockedIP}
   * @param {!BlockedIP} fbeValue Default value, defaults is new BlockedIP()
   * @returns {!BlockedIP} BlockedIP value
   */
  get (fbeValue = new BlockedIP()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBlockedIP}
   * @param {!BlockedIP} fbeValue BlockedIP value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IPv4Address.fbeSize) <= fbeStructSize) {
      this.IPv4Address.get(fbeValue.IPv4Address)
    } else {
      fbeValue.IPv4Address.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IPv4Address.fbeSize

    if ((fbeCurrentSize + this.FromInclusive.fbeSize) <= fbeStructSize) {
      fbeValue.FromInclusive = this.FromInclusive.get()
    } else {
      fbeValue.FromInclusive = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FromInclusive.fbeSize

    if ((fbeCurrentSize + this.ToExclusive.fbeSize) <= fbeStructSize) {
      fbeValue.ToExclusive = this.ToExclusive.get()
    } else {
      fbeValue.ToExclusive = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToExclusive.fbeSize

    if ((fbeCurrentSize + this.Reason.fbeSize) <= fbeStructSize) {
      fbeValue.Reason = this.Reason.get()
    } else {
      fbeValue.Reason = new BlockedIPReason()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Reason.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBlockedIP}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBlockedIP}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBlockedIP}
   * @param {!BlockedIP} fbeValue BlockedIP value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBlockedIP}
   * @param {BlockedIP} fbeValue BlockedIP value
   */
  setFields (fbeValue) {
    this.IPv4Address.set(fbeValue.IPv4Address)
    this.FromInclusive.set(fbeValue.FromInclusive)
    this.ToExclusive.set(fbeValue.ToExclusive)
    this.Reason.set(fbeValue.Reason)
  }
}

export { FieldModelBlockedIP };

/**
 * Fast Binary Encoding BlockedIP model
 */
class BlockedIPModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBlockedIP(this.buffer, 4)
  }

  /**
   * Get the BlockedIP model
   * @this {!BlockedIPModel}
   * @returns {!FieldModelBlockedIP} model BlockedIP model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BlockedIPModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BlockedIPModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BlockedIPModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BlockedIPModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBlockedIP.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BlockedIPModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BlockedIPModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BlockedIPModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BlockedIPModel}
   * @param {!BlockedIP} value BlockedIP value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BlockedIPModel}
   * @param {!BlockedIP} value BlockedIP value, defaults is new BlockedIP()
   * @return {!object} Deserialized BlockedIP value and its size
   */
  deserialize (value = new BlockedIP()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BlockedIP(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BlockedIP(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BlockedIPModel}
   * @param {!number} prev Previous BlockedIP model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BlockedIPModel };
